import React, { useEffect, useRef } from 'react';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { useCountUp } from '../../hooks/useCountUp';
import type { StarGoal } from '../../utils/starGoal';
import { Star } from './KidIcon';
import { KidButton, KidProgress } from './KidUI';
import { useSheet } from '../../hooks/useSheet';

/**
 * "Jens said yes! +5" — the full-screen message a child sees when a grown-up approves a task
 * (kid-first redesign step 3, mock-ups phone-07 / tablet-*-06). It stays until tapped; the
 * star total counts up from before the approval to now.
 */
export interface ApprovalMessageProps {
  approverName: string;
  taskTitle: string;
  points: number;
  totalStars: number;          // the child's star total now (after this approval)
  note?: string | null;        // the parent's note, already translated (step 6)
  goal: StarGoal | null;       // the child's star goal, if any
  onSeeReward: () => void;     // shown when the goal reward is ready
  onDone: () => void;          // acknowledges the message
  queueIndex?: number;         // 1-based, when several approvals arrive together
  queueSize?: number;
}

const initial = (name: string) => (name.trim().charAt(0) || '?').toLocaleUpperCase();

const ApprovalMessage: React.FC<ApprovalMessageProps> = ({
  approverName, taskTitle, points, totalStars, note, goal, onSeeReward, onDone, queueIndex, queueSize,
}) => {
  const { current_language } = useLanguage();
  const shownPoints = useCountUp(0, points, 900, taskTitle);
  const shownTotal = useCountUp(Math.max(0, totalStars - points), totalStars, 1000, taskTitle);
  const primaryRef = useRef<HTMLButtonElement>(null);
  useSheet(true); // the page behind stays still (Escape is handled below)

  // Focus the main button for keyboard / switch users; Escape = "Not now". Runs once per
  // message (the count-up re-renders every frame and the parent passes a new onDone each time).
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    primaryRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDoneRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const ready = !!goal?.ready;
  const goalLine = goal
    ? ready
      ? i18n.t('notifications.approved_reward_ready').replace('{{reward}}', goal.reward.title)
      : i18n.t('notifications.approved_more_to_goal').replace('{{n}}', String(goal.remaining)).replace('{{reward}}', goal.reward.title)
    : null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="approval-title"
      lang={current_language}
      className="fixed inset-0 z-[10060] overflow-y-auto bg-kid-bg dark:bg-kid-bg-dark text-kid-text dark:text-kid-text-dark"
    >
      {/* confetti */}
      <span className="absolute top-6 left-6 w-6 h-6 rounded-md bg-kid-green rotate-12" aria-hidden />
      <span className="absolute top-8 right-10 w-4 h-4 rounded-full bg-kid-orange" aria-hidden />
      <span className="absolute top-1/3 left-10 w-4 h-4 rounded-full bg-[#B79AE0]" aria-hidden />
      <span className="absolute top-1/4 right-6 w-5 h-5 rounded-md bg-[#7FB3F5] rotate-45" aria-hidden />

      <div className="relative min-h-full w-full max-w-[1180px] mx-auto px-4 md:px-8 py-6 md:py-10 flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
        {/* left / top: who, stars, task */}
        <div className="flex flex-col items-center text-center">
          {queueSize && queueSize > 1 && (
            <p className="text-sm font-bold mb-2">{queueIndex} / {queueSize}</p>
          )}
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 md:w-14 md:h-14 rounded-full border-2 border-kid-text dark:border-kid-text-dark bg-kid-tint-blue text-kid-text flex items-center justify-center font-kid-heading font-extrabold text-lg md:text-2xl" aria-hidden>
              {initial(approverName)}
            </span>
            <h2 id="approval-title" className="font-kid-heading font-extrabold text-3xl md:text-5xl leading-tight">
              {i18n.t('notifications.approved_said_yes').replace('{{admin}}', approverName)}
            </h2>
          </div>
          <div className="flex items-center gap-4 my-4 md:my-8" aria-label={`+${points}`}>
            <Star size={96} className="md:w-[140px] md:h-[140px]" />
            <span className="font-kid-heading font-extrabold text-7xl md:text-9xl leading-none" aria-hidden>+{shownPoints}</span>
          </div>
          <p className="text-xl md:text-2xl font-bold">{i18n.t('notifications.approved_for').replace('{{task}}', taskTitle)}</p>
        </div>

        {/* right / bottom: note, goal, buttons */}
        <div className="flex flex-col mt-6 lg:mt-0 flex-1 lg:flex-none">
          {note && (
            <div className="flex items-start gap-3 rounded-kid-card bg-kid-tint-blue text-kid-text px-4 py-3 md:px-6 md:py-4 mb-4 font-bold text-lg">
              <span aria-hidden>💬</span>
              <span>{i18n.t('notifications.approved_note').replace('{{admin}}', approverName).replace('{{note}}', note)}</span>
            </div>
          )}
          <div className="rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark p-4 md:p-6">
            <p className="font-kid-heading font-extrabold text-2xl md:text-3xl mb-3">
              {i18n.t('notifications.approved_now_you_have').replace('{{n}}', String(shownTotal))}
            </p>
            {goal && (
              <>
                <KidProgress value={Math.min(goal.have, goal.need)} max={goal.need} size="lg" label={goal.reward.title} />
                <p className={`mt-3 text-lg font-bold ${ready ? 'text-kid-green-shadow dark:text-green-300' : ''}`}>{goalLine}</p>
              </>
            )}
          </div>
          <div className="mt-auto lg:mt-8 pt-8 lg:pt-0 space-y-2">
            {ready ? (
              <>
                <KidButton ref={primaryRef} size="xl" fullWidth onClick={onSeeReward} trailing={<span aria-hidden>→</span>}>
                  {i18n.t('notifications.approved_see_reward')}
                </KidButton>
                <button type="button" onClick={onDone} className="block mx-auto min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300">
                  {i18n.t('notifications.approved_not_now')}
                </button>
              </>
            ) : (
              <KidButton ref={primaryRef} size="xl" fullWidth onClick={onDone}>
                {i18n.t('notifications.approved_ok')}
              </KidButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalMessage;
